'use client';

import React from 'react';
import { Typography } from '@workspace/ui/common';
import { processHeading } from '@workspace/lib/heading-utils';
import { cn, getBackgroundClass } from '@workspace/lib';
import type { MasterTextBlockProps } from './master-text-block.types';
import { CustomPortableText } from '@workspace/ui/PortableText';
import { RenderTextLink } from './RenderTextLink.ui';

export function MasterTextBlock({ block }: MasterTextBlockProps) {
  const { heading, hyperlink, description, layoutOptions, textBlocks = [], backgroundColor } = block;

  const { text: resolvedHeadingText } = processHeading(heading);
  const backgroundClass = getBackgroundClass(backgroundColor);

  return (
    <div className={cn('w-full', backgroundClass)}>
      <div className="porta-container">
        <section className={cn('md:py-3xl py-xl md:px-4xl px-mobile-input-y', backgroundClass)}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-lg md:gap-4xl">
            {/* Left Column - Heading and Link */}
            <div className="md:col-span-1">
              {/* Main Heading */}
              {resolvedHeadingText && (
                <Typography variant="h2" className={cn('mb-8 md:mb-xl')} styleVariant="h3">
                  {resolvedHeadingText}
                </Typography>
              )}

              {/* Main Description */}
              {description && <CustomPortableText className="md:mb-4 mb-2" value={description} />}

              {/* Hyperlink */}
              <RenderTextLink textLink={hyperlink} />
            </div>

            {/* Right Column - Text Blocks Grid */}
            {textBlocks && textBlocks.length > 0 && (
              <div className="md:col-span-2">
                <div
                  className={cn('grid grid-cols-1 gap-4 md:gap-8', {
                    'md:grid-cols-2': layoutOptions === 2,
                  })}
                >
                  {textBlocks.map((textBlock) => (
                    <div key={textBlock._key} className="space-y-2">
                      {/* Text Block Title */}
                      {textBlock.title && (
                        <Typography variant="h3" styleVariant="h6">
                          {textBlock.title}
                        </Typography>
                      )}

                      {/* Text Block Description */}
                      {textBlock.description && (
                        <div className="prose prose-lg max-w-none font-openSans">
                          <CustomPortableText value={textBlock.description} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
